import { CanvasMetadata } from '../services/cloudSave/canvasMetadata';

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format date in a user-friendly format
 */
export function formatDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Today at ' + dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffInDays === 1) {
    return 'Yesterday at ' + dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffInDays < 7) {
    return dateObj.toLocaleDateString([], { weekday: 'long' }) + ' at ' + 
           dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffInDays < 365) {
    return dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' at ' + 
           dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else {
    return dateObj.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  }
}

/**
 * Validate canvas name
 */
export function validateCanvasName(name: string): { isValid: boolean; error?: string } {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: 'Canvas name is required' };
  }
  
  if (name.trim().length < 3) {
    return { isValid: false, error: 'Canvas name must be at least 3 characters long' };
  }
  
  if (name.trim().length > 100) {
    return { isValid: false, error: 'Canvas name must be less than 100 characters' };
  }
  
  // Check for invalid characters
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(name)) {
    return { isValid: false, error: 'Canvas name contains invalid characters' };
  }
  
  return { isValid: true };
}

/**
 * Generate a default canvas name based on current date/time
 */
export function generateDefaultCanvasName(): string {
  const now = new Date();
  const dateStr = now.toLocaleDateString([], { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  return `Canvas ${dateStr}`;
}

/**
 * Sort canvases by different criteria
 */
export function sortCanvases(
  canvases: CanvasMetadata[], 
  sortBy: 'name' | 'lastModified' | 'createdAt' | 'fileSize' = 'lastModified',
  sortOrder: 'asc' | 'desc' = 'desc'
): CanvasMetadata[] {
  return [...canvases].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'lastModified':
        comparison = a.lastModified.toMillis() - b.lastModified.toMillis();
        break;
      case 'createdAt':
        comparison = a.createdAt.toMillis() - b.createdAt.toMillis();
        break;
      case 'fileSize':
        comparison = a.fileSize - b.fileSize;
        break;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });
}

/**
 * Filter canvases by search term
 */
export function filterCanvases(
  canvases: CanvasMetadata[], 
  searchTerm: string
): CanvasMetadata[] {
  if (!searchTerm.trim()) {
    return canvases;
  }
  
  const term = searchTerm.toLowerCase().trim();
  
  return canvases.filter(canvas => 
    canvas.name.toLowerCase().includes(term) ||
    canvas.description?.toLowerCase().includes(term) ||
    canvas.tags?.some(tag => tag.toLowerCase().includes(term))
  );
}

/**
 * Get canvas statistics
 */
export function getCanvasStats(canvases: CanvasMetadata[]) {
  const totalSize = canvases.reduce((sum, canvas) => sum + canvas.fileSize, 0);
  const totalCount = canvases.length;
  const oldestCanvas = canvases.length > 0 ? 
    canvases.reduce((oldest, current) => 
      current.createdAt.toMillis() < oldest.createdAt.toMillis() ? current : oldest
    ) : null;
  const newestCanvas = canvases.length > 0 ? 
    canvases.reduce((newest, current) => 
      current.lastModified.toMillis() > newest.lastModified.toMillis() ? current : newest
    ) : null;
  
  return {
    totalSize,
    totalCount,
    averageSize: totalCount > 0 ? totalSize / totalCount : 0,
    oldestCanvas,
    newestCanvas
  };
}

/**
 * Check if user has enough storage space (basic validation)
 */
export function checkStorageQuota(
  currentUsage: number, 
  newFileSize: number, 
  maxQuota: number = 100 * 1024 * 1024 // 100MB default
): { hasSpace: boolean; remainingSpace: number; error?: string } {
  const totalUsage = currentUsage + newFileSize;
  const remainingSpace = maxQuota - totalUsage;
  
  if (totalUsage > maxQuota) {
    return {
      hasSpace: false,
      remainingSpace,
      error: `File too large. You have ${formatFileSize(remainingSpace)} remaining space.`
    };
  }
  
  return {
    hasSpace: true,
    remainingSpace
  };
}

/**
 * Create a thumbnail from canvas data (placeholder for future implementation)
 */
export function createCanvasThumbnail(canvasData: any): string | null {
  // This is a placeholder for future thumbnail generation
  // Could use HTML5 Canvas to render a preview of the canvas
  // For now, return null to indicate no thumbnail
  return null;
}

/**
 * Sanitize canvas name for file system
 */
export function sanitizeCanvasName(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '_') // Replace invalid characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .trim();
}

/**
 * Parse tags from string input
 */
export function parseTags(tagString: string): string[] {
  if (!tagString.trim()) {
    return [];
  }
  
  return tagString
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)
    .slice(0, 10); // Limit to 10 tags
}

/**
 * Format tags for display
 */
export function formatTags(tags: string[]): string {
  if (!tags || tags.length === 0) {
    return '';
  }
  
  return tags.join(', ');
}
